import { getGatedIdacBackendUrl, getCurrentCloudType } from '../utilities/env';
import React, { useEffect } from 'react';
import makeAsyncScriptLoader from 'react-async-script';
import { buildState, LoginType, SocialLoginRecoveryOptions } from '../utilities/social-login';
import { CsrfToken, URLParameters } from '../types';
import queryString from 'query-string';
import submitFormHack from '../utilities/submit-form';
import { useAnalyticsClient } from '../utilities/analytics/analytics-web-client';

const oneTapClientUrl = 'https://accounts.google.com/gsi/client';

// API Reference:
// https://developers.google.com/identity/one-tap/web/reference/js-reference
export type GoogleAccountIdApi = {
  initialize(config: IdConfiguration): void;
  prompt(listener: (notification: PromptMomentNotification) => void): void;
  cancel(): void;
};

type GoogleOneTapAPI = {
  accounts?: {
    id?: GoogleAccountIdApi;
  };
};

type IdConfiguration = {
  client_id: string;
  use_fedcm_for_prompt?: boolean;
  cancel_on_tap_outside?: boolean;
  itp_support: false; // Don't show popup when 3P Cookies are blocked (eg. Safari)
  callback?: (response: CredentialResponse) => void;
};

interface CredentialResponse {
  credential: string;
}
type PromptMomentNotification = {
  isDismissedMoment(): boolean;
  getDismissedReason(): 'credential_returned' | 'cancel_called' | 'flow_restarted';
  getMomentType(): 'display' | 'skipped' | 'dismissed';
};

export type GoogleOneTapProps = {
  children?: React.ReactNode;
  google?: GoogleOneTapAPI;
  csrfToken: CsrfToken;
  googleAuthClientId: string;
  source: string;
  enabled: boolean;
  recoveryOptions?: SocialLoginRecoveryOptions;
};

export const GoogleOneTap: React.FC<GoogleOneTapProps> = ({
  children,
  csrfToken,
  enabled,
  google,
  googleAuthClientId,
  recoveryOptions,
  source,
}) => {
  const analyticsClient = useAnalyticsClient();

  useEffect(() => {
    const isCommercial = getCurrentCloudType() === 'commercial';

    if (google?.accounts?.id && enabled && isCommercial) {
      google.accounts.id.initialize({
        client_id: googleAuthClientId,
        cancel_on_tap_outside: false,
        use_fedcm_for_prompt: true,
        itp_support: false,
        callback: credentialResponse => {
          analyticsClient.buttonClickedEvent(source, 'googleYoloAccountSelected');
          const { userFlow }: URLParameters = queryString.parse(window.location.search);
          // [KIRBY-7902] TODO: use feature flag to determine if we should use the new endpoint (and submitFormHackReplacement) or the old one
          // Backend URL and endpoint to be in a new place if it requires changes to accept and response with JSON, but the owner of the service is outside of Kirby.
          const callbackBase = getGatedIdacBackendUrl('/login/google', {
            legacyPath: `${window.location.origin}/login/google`,
          });
          const callbackParams = {
            state: buildState({
              location: window.location,
              csrfToken,
              source,
              loginType: LoginType.GoogleOneTap,
              anonymousId: analyticsClient.getAnonymousId(),
              userFlow,
              recoveryOptions,
            }),
          };

          // [KIRBY-7902] TODO: replace with `submitFormHackReplacement` once the backend endpoint is ready.
          submitFormHack({
            method: 'POST',
            action: `${callbackBase}?${queryString.stringify(callbackParams)}`,
            payload: { id_token: credentialResponse.credential },
          });
        },
      });

      google.accounts.id.prompt(notification => {
        if (notification.isDismissedMoment()) {
          const reason = notification.getDismissedReason();
          analyticsClient.buttonClickedEvent(source, 'googleYoloDismissed', { reason });
        }
      });
    }

    return () => google?.accounts?.id?.cancel();
  }, [google, enabled, googleAuthClientId, csrfToken, analyticsClient, source, recoveryOptions]);

  return <>{children}</>;
};

export default makeAsyncScriptLoader<GoogleOneTapProps>(oneTapClientUrl, {
  globalName: 'google',
  attributes: {
    nonce: window.cspNonce,
  },
})(GoogleOneTap);
